import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import ApprovalsManage from './components/ApprovalsManage';
import ApprovalsReview from './components/ApprovalsReview';
import LottieComponent from '@components/Lottie';
import { useCurrentWorkspace, useDefaultWorkspace } from '@hooks/workspace';
import {
  ApprovalWorkflowControllerCountApprovalWorkflowsParams,
  ApprovalWorkflowControllerListApprovalWorkflowsParams
} from '@api/Api';
import { useDebounced } from '@helpers/useDebounced';
import {
  useGetCountApprovalWorkflows,
  useGetListApprovalWorkflows
} from '@hooks/queries/approval';
import './Approvals.less';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';

const { TabPane } = Tabs;

function ApprovalsInWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabKey = searchParams.get('tab') || 'manage';
  const [workspace] = useCurrentWorkspace();
  const sortByParam = (searchParams.get('sortBy') ||
    'default') as ApprovalWorkflowControllerListApprovalWorkflowsParams['orderBy'];
  const campaignIdsParam = searchParams.getAll('campaignIds');
  const statusesParam = searchParams.getAll('statuses');
  const dueDateParam = searchParams.getAll('dueDate');
  const managersParam = searchParams.getAll('managers');
  const createdDateParam = searchParams.getAll('createdDate');
  const searchParam = searchParams.get('search') || undefined;
  const searchText = useDebounced(searchParam, 600);

  const approvalWorkflowsParams = useMemo(() => {
    return {
      searchQuery: searchText,
      campaignId: campaignIdsParam.length ? campaignIdsParam : undefined,
      dueDateFrom: dueDateParam?.[1] ? dueDateParam[0] : undefined,
      dueDateTo: dueDateParam?.[1] ? dueDateParam[1] : undefined,
      createdAtFrom: createdDateParam?.[1] ? createdDateParam[0] : undefined,
      createdAtTo: createdDateParam?.[1] ? createdDateParam[1] : undefined,
      managers: managersParam.length ? managersParam : undefined,
      statuses: statusesParam.length
        ? (statusesParam as ApprovalWorkflowControllerCountApprovalWorkflowsParams['statuses'])
        : undefined
    };
  }, [
    managersParam,
    statusesParam,
    dueDateParam,
    createdDateParam,
    campaignIdsParam,
    searchText
  ]);

  const { data: allApprovalsCount, refetch: refetchAllApprovalsCount } =
    useGetCountApprovalWorkflows({
      params: {
        workspaceId: workspace.id
      }
    });

  const { data: approvalsCount, refetch: refetchApprovalsCount } =
    useGetCountApprovalWorkflows({
      params: {
        workspaceId: workspace.id,
        ...(activeTabKey === 'manage' ? approvalWorkflowsParams : {})
      }
    });

  const prevActiveTabKey = React.useRef(activeTabKey);

  const { data: approvalsReviewerCount } = useGetCountApprovalWorkflows({
    keepPreviousData: activeTabKey === prevActiveTabKey.current,
    params: {
      workspaceId: workspace.id,
      isReviewer: true,
      ...(activeTabKey === 'review' ? approvalWorkflowsParams : {})
    }
  });

  const {
    data: approvals,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPageApprovals,
    refetch: refetchApprovals,
    fetchNextPage: fetchNextPageApprovals
  } = useGetListApprovalWorkflows({
    keepPreviousData: activeTabKey === prevActiveTabKey.current,
    params: {
      workspaceId: workspace.id,
      orderBy: sortByParam,
      ...(activeTabKey === 'review'
        ? {
            isReviewer: true
          }
        : {}),
      ...approvalWorkflowsParams
    }
  });

  if (activeTabKey !== prevActiveTabKey.current) {
    prevActiveTabKey.current = activeTabKey;
  }

  const approvalsList = useMemo(() => {
    return approvals?.pages.flatMap((x) => x.edges.map((x) => x.node)) ?? [];
  }, [approvals?.pages]);

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => {
      refetchAllApprovalsCount();
      refetchApprovalsCount();
      refetchApprovals();
    },
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspace.id) return false;
      return !msg.causedByMe;
    },
    'aw-created',
    'aw-updated',
    'aw-removed'
  );

  return (
    <div className="approvals-page">
      <div className="approvals-page_head">
        ALL APPROVALS
        <div className="approvals-page_head-count">{allApprovalsCount}</div>
      </div>
      <div className="approvals-page_text">
        View all approvals created across all projects here.
      </div>
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => {
          setSearchParams({ tab: key });
        }}
        className="approvals-page_tabs"
      >
        <TabPane
          tab={
            <>
              Manage approvals
              <span className="count">{approvalsCount}</span>
            </>
          }
          key="manage"
        >
          <ApprovalsManage
            approvalsList={approvalsList}
            hasNextPageApprovals={!!hasNextPageApprovals}
            approvalWorkflowsParams={approvalWorkflowsParams}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            refetchApprovals={refetchApprovals}
            fetchNextPageApprovals={fetchNextPageApprovals}
          />
        </TabPane>
        <TabPane
          tab={
            <>
              Review approvals
              <span className="count">{approvalsReviewerCount}</span>
            </>
          }
          key="review"
        >
          <ApprovalsReview
            approvalsList={approvalsList}
            hasNextPageApprovals={!!hasNextPageApprovals}
            approvalWorkflowsParams={approvalWorkflowsParams}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPageApprovals={fetchNextPageApprovals}
          />
        </TabPane>
      </Tabs>
    </div>
  );
}

export default function Approvals() {
  const { workspaceId: workspaceIdParam } = useParams();
  const defaultWorkspace = useDefaultWorkspace(false);
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const workspaceId = currentWorkspace?.id;
  const [allow404, setAllow404] = useState(false);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!workspaceIdParam || workspaceIdParam === workspaceId) {
      setResolved(true);
      return;
    }
    if (!defaultWorkspace) {
      setResolved(false);
      return;
    }
    setResolved(false);
    setAllow404(false);
    setCurrentWorkspace(workspaceIdParam)
      .catch(() => setAllow404(true))
      .finally(() => setResolved(true));
  }, [workspaceIdParam, workspaceId, defaultWorkspace, setCurrentWorkspace]);

  const fullPageLoader = (
    <div className="page_spinner_overlay">
      <LottieComponent className="page_spinner" view="preloader" />
    </div>
  );

  if (!defaultWorkspace) {
    return <Navigate to="/" replace />;
  }
  if (!workspaceIdParam) {
    return <Navigate to={`/approvals/${defaultWorkspace.id}`} replace />;
  }
  if (!workspaceId) {
    if (!resolved) return fullPageLoader;
    if (!allow404) return fullPageLoader;
    return <Navigate to="/error-404" replace />;
  }
  if (workspaceId !== workspaceIdParam) {
    if (!allow404) return fullPageLoader;
    return <Navigate to="/error-404" replace />;
  }
  return <ApprovalsInWorkspace />;
}
